import React, { useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth, isViewingAs, VIEWING_AS_NOTE } from '../context/AuthContext';
import { CheckCircle, AlertTriangle, Users, Shield, LogIn } from 'lucide-react';
import './AuthPages.css';

// Accept page for a co-parent / co-guardian invite (kind = 'coparent').
// Backend: GET  /clubs/:slug/coparent-invites/:token        (public preview)
//          POST /clubs/:slug/coparent-invites/:token/accept (auth required)
// The link is emailed as /:clubSlug/coparent-accept/:token.
const CoparentAcceptPage = () => {
  const { clubSlug, token } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, impersonating, loading: authLoading } = useAuth();
  const viewingAs = isViewingAs(impersonating); // B-65 review: the server refuses it

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const { data: invite, isLoading, isError, error: queryError } = useQuery({
    queryKey: ['coparent-invite', clubSlug, token],
    queryFn: () => axios.get(`/clubs/${clubSlug}/coparent-invites/${token}`).then(r => r.data),
    retry: false,
  });

  const handleAccept = async () => {
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`/clubs/${clubSlug}/coparent-invites/${token}/accept`);
      setSuccess(true);
      setTimeout(() => navigate(`/${clubSlug}/account`), 1800);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally { setSubmitting(false); }
  };

  // WT-1 review: and while a stored session loads, so a signed-in co-parent
  // isn't offered Sign in / Create account first.
  if (isLoading || authLoading) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="page-loading"><div className="spinner" style={{ width: 40, height: 40 }} /></div>
          <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Loading invitation…</p>
        </div>
      </div>
    );
  }

  if (isError) {
    // 410 = already used / expired; 404 = not found. Give a tailored message.
    const status = queryError?.response?.status;
    const msg = status === 410
      ? (queryError?.response?.data?.error || 'This invite has already been used or has expired.')
      : 'This invite link is no longer valid. Ask whoever sent it to share a new one.';
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <AlertTriangle size={40} style={{ color: 'var(--danger)', marginBottom: 16 }} />
          <h2>Invite unavailable</h2>
          <p style={{ color: 'var(--text-secondary)', marginTop: 8 }}>{msg}</p>
          <Link to="/" className="btn btn-primary" style={{ marginTop: 20 }}>Go to Marakin</Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <CheckCircle size={48} style={{ color: 'var(--success)', marginBottom: 16 }} />
          <h2>You're linked!</h2>
          <p style={{ color: 'var(--text-secondary)', marginTop: 8 }}>
            You can now help manage {invite.childName || 'your child'} at {invite.clubName}. Taking you to your account…
          </p>
        </div>
      </div>
    );
  }

  const childName = invite.childName || 'a child';
  const inviterName = invite.inviterName || 'A parent';

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ display: 'inline-flex', padding: 14, borderRadius: '50%', background: 'rgba(30,74,53,0.08)', marginBottom: 12 }}>
            <Users size={28} style={{ color: '#1E4A35' }} />
          </div>
          <h2 style={{ margin: 0 }}>Help look after {childName}</h2>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 8 }}>
            <span className="badge badge-green"><Shield size={12} /> Parent / Guardian</span>
          </div>
          <p style={{ color: 'var(--text-secondary)', marginTop: 10, fontSize: 14 }}>
            {inviterName} has invited you to be a parent / guardian for <strong>{childName}</strong> at <strong>{invite.clubName}</strong>.
            Once linked, you'll be able to see {childName}'s events and respond on their behalf.
          </p>
        </div>

        {error && (
          <div className="auth-error" style={{ marginBottom: 14 }}>
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        {user && viewingAs ? (
          <p className="muted" style={{ textAlign: 'center', fontSize: 14 }}>{VIEWING_AS_NOTE}</p>
        ) : user ? (
          <button
            className="btn btn-primary"
            disabled={submitting}
            onClick={handleAccept}
            style={{ width: '100%', justifyContent: 'center', background: '#1E4A35' }}
          >
            {submitting ? 'Linking…' : <><CheckCircle size={15} /> Accept &amp; link my account</>}
          </button>
        ) : (
          <>
            <p style={{ textAlign: 'center', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 14 }}>
              Sign in or create your account to accept — you'll come straight back here.
            </p>
            <Link
              to="/login"
              state={{ from: location.pathname }}
              className="btn btn-primary"
              style={{ width: '100%', justifyContent: 'center', background: '#1E4A35' }}
            >
              <LogIn size={15} /> Log in to accept
            </Link>
            <Link
              to="/register"
              state={{ from: location.pathname }}
              className="btn btn-secondary"
              style={{ width: '100%', justifyContent: 'center', marginTop: 10 }}
            >
              Create an account
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default CoparentAcceptPage;
